import api from 'src/shared/api/axios';
import type { WbsItemVersion } from '../types';
import type { WbsListWithItems, SaveDraftItemInput, SaveDraftItemResult, DraftItem } from './types.local';
import { normalizeDateOnly } from 'src/utils/date';
// import { DraftItem, } from './WbsEditorContainer';

// ⬇️ 유틸: 404면 { list:null, items: [] }로 돌려줌
function emptyList(): WbsListWithItems {
    return { list: null, items: [] };
}

// [추가] 버전 메타
export type WbsListMeta = {
    version: number;
    isCurrent: boolean;
    createdAt: string;
    activatedAt?: string | null;
};

// (히스토리 응답 타입이 중앙 파일에 없다면 아래처럼 로컬에서 정의하거나 백엔드 스키마에 맞춰 별도 타입을 둡니다)
type NodeHistoryItem = {
    id: string;
    nodeId: string;
    name: string;
    createdAt: string; // ISO
    publishedListVersion: number | null;
    orderIndex: number | null;
    level: number | null;
    code: string | null;
};

type WbsItemDraftPayload = {
    parentNodeId?: string | null;
    orderIndex?: number | null;
    name?: string | null;
    plannedStart?: string | null;
    plannedFinish?: string | null;
    weight?: number | null;
    memo?: string | null;
    isDeleted?: boolean;
};

function normalizeDraftPayload<T extends { plannedStart?: any; plannedFinish?: any }>(x: T): T {
    return {
        ...x,
        plannedStart: normalizeDateOnly(x.plannedStart),
        plannedFinish: normalizeDateOnly(x.plannedFinish),
    };
}

/** 드래프트 읽기 */
// 호출하는 곳 있음
export async function listDraftItems(projectId: string): Promise<DraftItem[]> {
    try {
        const { data } = await api.get(`/projects/${projectId}/wbs/drafts`);
        return data.data ?? data;
    } catch (err: any) {
        const serverMsg = err?.response?.data ?? err?.message;
        console.error('LIST_DRAFT_ITEMS_RESPONSE: ', serverMsg);
        throw err;
    }
}

/**
 * 현재 활성 리스트 조회
 * - 404: 프로젝트에 아직 발행/드래프트 리스트가 전혀 없을 수 있으므로 빈 상태로 매핑
 * - 기타 오류(401/403/500): 상위에서 처리
 */
export async function apiGetCurrentList(projectId: string): Promise<WbsListWithItems> {
    try {
        const { data } = await api.get(`/projects/${projectId}/wbs/lists/current`);
        // const { data } = await api.get(`/projects/${projectId}/wbs/editor/effective`);
        return data.data ?? data;
    } catch (err: any) {
        if (err?.response?.status === 404) return emptyList(); // ✅ 최초 진입 대비
        throw err;
    }
}

export async function apiBootstrapLatestDrafts(projectId: string) {
    try {
        const { data } = await api.post(`/projects/${projectId}/wbs/drafts/bootstrap-latest`);
        return data.data ?? data;
    } catch (err: any) {
        throw err;
    }
}

export async function apiDiscardAllDrafts(projectId: string) {
    try {
        const { data } = await api.delete(`/projects/${projectId}/wbs/drafts`);
        return data.data ?? data;
    } catch (err: any) {
        throw err;
    }
}

export async function apiRebuildDraftHierarchy(projectId: string) {
    const { data } = await api.post(`/projects/${projectId}/wbs/drafts/rebuild-hierarchy`);
    return data.data ?? data;
}

/** 특정 버전(또는 current) 읽기 */
// 호출하는 곳 있음
export async function getCurrentPublished(projectId: string): Promise<WbsListWithItems> {
    try {
        const { data } = await api.get(`/projects/${projectId}/wbs/current`);
        return data?.data ?? data ?? emptyList();
    } catch (err: any) {
        const serverMsg = err?.response?.data ?? err?.message;
        console.error('GET_CURRENT_PUBLISHED_RESPONSE: ', serverMsg);
        throw err;
    }
}

// 호출하는 곳 있음 (기존 apiListVersion -> getAllListVersions로 이름 변경함)
export async function getAllListVersions(projectId: string): Promise<WbsListMeta[]> {
    try {
        // const { data } = await api.get(`/projects/${projectId}/wbs/versions`);
        const { data } = await api.get(`/projects/${projectId}/wbs/lists`);
        return data?.data ?? data ?? [];
    } catch (err: any) {
        if (err?.response?.status === 404) return [];
        throw err;
    }
}

// 호출하는 곳 있음
export async function createItem(
    projectId: string,
    payload: {
        parentNodeId?: string | null;
        name: string;
        orderIndex?: number;
        plannedStart?: string | null;
        plannedFinish?: string | null;
        weight?: number | null;
        memo?: string | null;
        // isDeleted?: boolean;
    },
) {
    try {
        const { data } = await api.post(`/projects/${projectId}/wbs/items`, normalizeDraftPayload(payload));
        return data.data ?? data;
    } catch (err: any) {
        const serverMsg = err?.response?.data ?? err?.message;
        console.error('CREATE_DRAFT_WBS_ERROR_RESPONSE: ', serverMsg);
        throw err;
    }
}

// 호출하는 곳 있음
export async function updateItem(projectId: string, nodeId: string, patch: WbsItemDraftPayload) {
    try {
        const { data } = await api.patch(`/projects/${projectId}/wbs/items/${nodeId}`, normalizeDraftPayload(patch));
        return data.data ?? data;
    } catch (err: any) {
        const serverMsg = err?.response?.data ?? err?.message;
        console.error('UPDATE_DRAFT_WBS_ERROR_RESPONSE: ', serverMsg);
        throw err;
    }
}

// 호출하는 곳 있음
// export async function deleteDraftWbs(projectId: string, nodeId: string) {
//     try {
//         const { data } = await api.delete(`/projects/${projectId}/wbs/drafts/${nodeId}`);
//         return data?.data ?? data;
//     } catch (err: any) {
//         const serverMsg = err?.response?.data ?? err?.message;
//         console.error('DELETE_DRAFT_WBS_ERROR_RESPONSE: ', serverMsg);
//         throw err;
//     }
// }

// 추가
export async function deleteItem(projectId: string, nodeId: string) {
    try {
        const { data } = await api.delete(`/projects/${projectId}/wbs/items/${nodeId}`);
        return data.data ?? data;
    } catch (err: any) {
        const serverMsg = err?.response?.data ?? err?.message;
        console.error('UPDATE_DRAFT_WBS_ERROR_RESPONSE: ', serverMsg);
        throw err;
    }
}

// 호출하는 곳 있음
export async function removeItemWithOp(projectId: string, nodeId: string, op: 'discard' | 'retire' | 'auto' = 'auto') {
    const { data } = await api.delete(`/projects/${projectId}/wbs/items/${nodeId}`, { params: { op } });
    return data.data ?? data;
}

export async function reorderSiblings(
    projectId: string,
    pid: string | null,
    items: Array<{ nodeId: string; orderIndex: number }>,
) {
    const parentNodeId = pid ?? 'root';
    const { data } = await api.post(`/projects/${projectId}/wbs/parents/${parentNodeId}/reorder`, { items });
    return data.data ?? data;
}

export async function apiPublish(projectId: string): Promise<{ version: number }> {
    try {
        const { data } = await api.post(`/projects/${projectId}/wbs/lists/publish`, {});
        return data.data ?? data;
    } catch (err: any) {
        const serverMsg = err?.response?.data ?? err?.message;
        console.error('PUBLISH_ERROR_RESPONSE: ', serverMsg);
        throw err;
    }
}

// 추가
export async function fetchEditorItems(projectId: string) {
    const { data } = await api.get(`/projects/${projectId}/wbs/editor/effective`);
    return data; // effective WbsItemVersion[]
}

// 추가
export async function fetchVersionItems(projectId: string, version: number) {
    const { data } = await api.get(`/projects/${projectId}/wbs/versions/${version}`);
    return data; // snapshot WbsItemVersion[]
}

// export async function createItemDraft(
//     projectId: string,
//     payload: {
//         parentNodeId: string | null;
//         name: string;
//     },
// ) {
//     const { data } = await api.post(`/projects/${projectId}/wbs/items`, payload);
//     return data?.data ?? data;
// }

// [추가] current발행본 + items
// export async function apiGetCurrentList(projectId: string) {
//     const { data } = await api.get(`/projects/${projectId}/wbs/lists/current`);
//     return data?.data ?? data;  // { list, items } | null
// }

// [추가] 특정 버전 활성화
export async function apiActivateVersion(projectId: string, version: number) {
    await api.post(`/projects/${projectId}/wbs/lists/${version}/activate`, {});
}

/**
 * 특정 버전의 리스트 조회
 * - 404: 요청한 버전이 없을 때도 처음 사용성을 위해 빈 상태로 보여줌
 *   (원한다면 'strict' 옵션을 추가해 404를 그대로 에러로 던지도록 확장 가능)
 */
export async function apiGetListByVersion(projectId: string, version: number): Promise<WbsListWithItems> {
    try {
        const { data } = await api.get(`/projects/${projectId}/wbs/lists/${version}`);
        return data.data ?? data;
    } catch (err: any) {
        if (err?.response?.status === 404) return emptyList(); // ✅ UX 우선
        throw err;
    }
}

export async function apiCreateDraft(projectId: string, payload: Partial<WbsItemVersion>) {
    const { data } = await api.post(`/projects/${projectId}/wbs/items`, payload);
    return data.data ?? data;
}

/** @deprecated bootstrap + batch(update-only)로 통일됨. 사용 금지 */
export async function apiSaveDraft(projectId: string, nodeId: string, payload: Partial<WbsItemVersion>) {
    throw new Error('DEPRECATED: Do not call apiSaveDraft(). Use apiSaveDraftBatch() (update-only).');
    // eslint-disable-next-line no-unreachable
    const { data } = await api.post(`/projects/${projectId}/wbs/items/${nodeId}/draft`, payload);
    return data.data ?? data;
}

/** @deprecated bootstrap + batch(update-only)로 통일됨. 사용 금지 */
export async function apiDeleteDraft(projectId: string, nodeId: string) {
    throw new Error('DEPRECATED: Do not call apiDeleteDraft(). Use discard drafts API or batch update.');
    // eslint-disable-next-line no-unreachable
    const { data } = await api.delete(`/projects/${projectId}/wbs/items/${nodeId}/draft`);
    return data.data ?? data;
}

export async function apiRequestDelete(projectId: string, nodeId: string, mode: 'single' | 'subtree' = 'subtree') {
    const { data } = await api.post(`/projects/${projectId}/wbs/items/${nodeId}/delete-request?mode=${mode}`);
    return data.data ?? data;
}
export async function apiListChanges(projectId: string) {
    const { data } = await api.get(`/projects/${projectId}/wbs/changes`);
    return data.data ?? data;
}

export async function apiGetItemHistory(projectId: string, nodeId: string): Promise<NodeHistoryItem[]> {
    const { data } = await api.get(`/projects/${projectId}/wbs/items/${nodeId}/history`);
    return data.data ?? data;
}

// const toISO = (d: string | null | undefined) => (d ? new Date(d).toISOString() : null);
const normalize = (x: any) => {
    const out: any = { ...x };
    if (out.plannedStart !== undefined) {
        out.plannedStart = normalizeDateOnly(out.plannedStart);
    }
    if (out.plannedFinish !== undefined) {
        out.plannedFinish = normalizeDateOnly(out.plannedFinish);
    }
    // weight는 0 ~ 1 -> 0 ~ 100으로 정책 변경함
    // if (out.weight != null && out.weight > 1) {
    //     out.weight = out.weight / 100; // % -> 0 ~ 1
    // }
    return out;
};

export async function apiSaveDraftBatch(
    projectId: string,
    items: SaveDraftItemInput[],
): Promise<SaveDraftItemResult[]> {
    const body = { items: items.map(normalize) };
    const { data } = await api.post(`/projects/${projectId}/wbs/draft/batch`, body);
    return data.data ?? data;
}
